import '@shared/dream-components/style.css';
import '~/tailwind.css';

import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

import { ManifestLink } from '@remix-pwa/sw';
import { Links, Meta, Scripts, useLocation, useRouteLoaderData, useSearchParams } from '@remix-run/react';
import cx from 'classnames';

import { AppGlobalsScript } from '~/components/AppGlobals';
import FacebookPixel from '~/components/Pixels/FacebookPixel';
import GA4Pixel from '~/components/Pixels/GA4Pixel';
import { GTMPixel, GTMScript } from '~/components/Pixels/GTMPixel';
import TiktokPixel from '~/components/Pixels/TiktokPixel';
import TwitterPixel from '~/components/Pixels/TwitterPixel';
import { useDisplayMode } from '~/hooks/useDisplayMode';
import useCookies from '~/hooks/useUtils/useCookies';
import type { RootLoaderData } from '~/root';
import getRootStyles from '~/utils/getRootStyles';

import { ComponentErrorBoundary } from '../ComponentErrorBoundary';
import EmbedSupportScript from '../EmbedSupportScript';
import { FontStyles } from '../FontStyles';
import GlobalHeaderScript from '../Pixels/GlobalHeaderScript';
import GSCVerification from '../Pixels/GSCVerification';
import { ScrollRestoration } from '../ScrollRestoration';

type Props = PropsWithChildren<{
  title?: string;
}>;

const Document = ({ children }: Props) => {
  const { site, isProdEnv, noScript } = (useRouteLoaderData('root') ?? {}) as RootLoaderData;

  const location = useLocation();
  const { i18n } = useTranslation();

  const [searchParams] = useSearchParams();
  const preventScrollOnFilter = searchParams.has('tags') || (searchParams.has('q') && location.pathname === '/');

  const isPostPage = location.pathname.startsWith('/p/');

  let bodyClass = '';

  if (isPostPage) {
    bodyClass = 'bg-wt-background text-wt-text-on-background';
  }
  const gtmPixel = site?.viewable_site_version?.settings?.gtm_pixel_id;

  return (
    <html lang={i18n?.language || 'en'} className="h-full antialiased">
      <Head />
      <body suppressHydrationWarning className={cx(bodyClass)}>
        {isProdEnv && gtmPixel && (
          <ComponentErrorBoundary
            fallbackRender={() => {
              return null;
            }}
          >
            <GTMPixel pixelId={gtmPixel} />
          </ComponentErrorBoundary>
        )}
        {/** Prevent any user clicks when previewing so the user doesn't navigate to the wrong page in the iframe */}
        {children}
        {!preventScrollOnFilter && <ScrollRestoration />}
        {!noScript && <Scripts />}
      </body>
    </html>
  );
};

export default Document;

export function Head() {
  const { site, publication, isProdEnv, rootStyles } = (useRouteLoaderData('root') ?? {}) as RootLoaderData;
  const displayMode = useDisplayMode();
  const location = useLocation();
  const { cookies } = useCookies({ initialCookies: {} });

  // Compliance
  const cookiesBanner = site?.viewable_site_version?.settings?.cookie_banner_enabled;
  const cookiesLocalStorage = cookies?.beehiiv_honey;

  let marketingCookies = !cookiesBanner;
  let dataProcessorCookies = !cookiesBanner;

  if (cookiesBanner) {
    marketingCookies =
      cookiesBanner && cookiesLocalStorage
        ? JSON.parse(cookiesLocalStorage)?.marketing || false
        : !(cookiesBanner && !cookiesLocalStorage);
    dataProcessorCookies =
      cookiesBanner && cookiesLocalStorage
        ? JSON.parse(cookiesLocalStorage)?.dataProcessing || false
        : !(cookiesBanner && !cookiesLocalStorage);
  }

  // Pixels
  const facebookPixel = site?.viewable_site_version?.settings?.meta_pixel_id;
  const twitterPixel = site?.viewable_site_version?.settings?.x_pixel_id;
  const tiktokPixel = site?.viewable_site_version?.settings?.tiktok_pixel_id;
  // TODO: Check with Mike if this is deprecated?
  // const gaPixel = publication?.google_analytics_pixel;
  const ga4Pixel = site?.viewable_site_version?.settings?.ga4_pixel_id;
  const gtmPixel = site?.viewable_site_version?.settings?.gtm_pixel_id;
  const gscCode = site?.viewable_site_version?.settings?.gsc_verification_code;
  const globalScript = site?.viewable_site_version?.settings?.global_header_script_blob;

  const viewport = `width=device-width, initial-scale=1, shrink-to-fit=no${displayMode === 'standalone' ? ', user-scalable=no' : ''}`;
  const isPostPage = location.pathname.startsWith('/p/');

  return (
    <head>
      <meta charSet="utf-8" />
      <meta name="viewport" content={viewport} />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="theme-color" content="#000000" />
      <AppGlobalsScript />
      {/* set default colors in case root loader fails and cant return root styles for error boundary*/}
      {/* these colors will be overridden below from getRootStyles() from root loader*/}
      <style
        dangerouslySetInnerHTML={{
          __html: `
:root {
  --wt-primary-color: #030712;
  --wt-text-on-primary-color: #F9FAFB;

  --wt-background-color: #FFFFFF;
  --wt-text-on-background-color: #030712;
}
`,
        }}
      />
      {publication?.web_template?.pwa_enabled && (
        <>
          <script
            dangerouslySetInnerHTML={{
              __html: `
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  window.__DeferredPrompt__ = e;
});
          `,
            }}
          />
          <ManifestLink />
        </>
      )}
      <Meta />
      <FontStyles />
      <style
        suppressHydrationWarning={true}
        dangerouslySetInnerHTML={{
          __html:
            isPostPage && publication?.web_theme
              ? getRootStyles(
                  publication,
                  site?.viewable_site_version?.settings?.post_page_background_color,
                  site?.viewable_site_version?.settings?.post_page_text_color,
                )
              : rootStyles,
        }}
      />
      {/* <style id="tailwind-inline" dangerouslySetInnerHTML={{ __html: tailwindCss }} />
      <style id="dream-inline" dangerouslySetInnerHTML={{ __html: dreamCss }} /> */}
      <link rel="apple-touch-icon" href={publication?.logo?.thumb?.url} />
      <link rel="apple-touch-startup-image" href={publication?.thumbnail?.url} />
      <Links />
      <CriticalCss />
      {isProdEnv && (
        <ComponentErrorBoundary
          fallbackRender={() => {
            return null;
          }}
        >
          {gscCode && <GSCVerification code={gscCode} />}
          {gtmPixel && <GTMScript dataProcessingCookies={dataProcessorCookies} pixelId={gtmPixel} />}
          {facebookPixel && <FacebookPixel marketingCookies={marketingCookies} pixelId={facebookPixel} />}
          {twitterPixel && <TwitterPixel marketingCookies={marketingCookies} pixelId={twitterPixel} />}
          {tiktokPixel && <TiktokPixel marketingCookies={marketingCookies} pixelId={tiktokPixel} />}
          {ga4Pixel && <GA4Pixel marketingCookies={marketingCookies} pixelId={ga4Pixel} />}
        </ComponentErrorBoundary>
      )}
      {globalScript && <GlobalHeaderScript script={globalScript} />}
      <EmbedSupportScript />
    </head>
  );
}

function CriticalCss() {
  if (process.env.NODE_ENV !== 'production') {
    return null;
  }
  // on the server, we'll inject the critical css in entry.server.tsx
  // on the client, we'll just return the contents of the style tag that is currently in the DOM
  // this will prevent hydration mismatches
  let criticalCss = '';
  if (typeof window !== 'undefined') {
    criticalCss = document.getElementById('critical-css')?.innerHTML || '';
  }
  return <style id="critical-css" dangerouslySetInnerHTML={{ __html: criticalCss }} />;
}
